import React from 'react';
import {
  Snackbar,
  Alert,
  AlertColor,
  Slide,
  SlideProps,
  Box,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { hideToast, selectToasts } from '@/store/slices/uiSlice';

interface SlideTransitionProps extends Omit<SlideProps, 'direction'> {}

function SlideTransition(props: SlideTransitionProps) {
  return <Slide {...props} direction="left" />;
}

const ToastNotification: React.FC = () => {
  const dispatch = useDispatch();
  const toasts = useSelector(selectToasts);
  const currentToast = toasts[0]; // Show the first toast in queue

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    
    if (currentToast) {
      dispatch(hideToast(currentToast.id));
    }
  };

  const getAlertSeverity = (type: string): AlertColor => {
    switch (type) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
      default:
        return 'info';
    }
  };

  if (!currentToast) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: { xs: 80, sm: 90 }, // Account for app bar height
        right: 16,
        zIndex: (theme) => theme.zIndex.snackbar,
        maxWidth: { xs: 'calc(100vw - 32px)', sm: 400 },
      }}
    >
      <Snackbar
        open={true}
        autoHideDuration={currentToast.duration || 2000}
        onClose={handleClose}
        TransitionComponent={SlideTransition}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          position: 'static',
          transform: 'none',
        }}
      >
        <Alert
          onClose={handleClose}
          severity={getAlertSeverity(currentToast.type)}
          variant="filled"
          sx={{
            width: '100%',
            fontSize: '0.875rem',
            '& .MuiAlert-message': {
              // Allow multi-line display without restrictions
              display: 'block',
            },
          }}
        >
          <Box>
            <Box component="span" sx={{ fontWeight: 'bold', display: 'block' }}>
              {currentToast.title}
            </Box>
            <Box component="span" sx={{ fontSize: '0.8125rem', opacity: 0.9 }}>
              {currentToast.message}
            </Box>
          </Box>
        </Alert>
      </Snackbar>
      
      {/* Toast Queue Indicator */}
      {toasts.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '50%',
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            color: 'white',
            pointerEvents: 'none',
          }}
        >
          {toasts.length}
        </Box>
      )}
    </Box>
  );
};

export default ToastNotification;
